import { ConstantProperty } from "cesium";
import { AlignmentPointType } from "../../core/alignment/interface";
import { getDefaultSectionMarkerColorSettings, } from "../../settings/earthbrain-site-settings";
import { toSectionMarkers } from "../../site/alignment/alignment-section-marker-adapter";
import { SectionMarkerRenderer, } from "../../site/alignment/section-marker-renderer";
import { getMarkerCurrentId, getMarkerDistanceById, getMarkerIdAt, getMarkerLabel, hasCenterLinePointAt, isIpAssociatedMarker, } from "../../site/alignment/section-marker-resolver";
import { CESIUM_PICKABLE_ENTITY, getEntityPickInfo } from "../../types";
import { ActivityLogger } from "../../utilities/activity-logger";
/** Owns the selected section during an edit: tracking across edits, sphere highlight and 3D panel. */
export class SectionSelectionController {
    _host;
    _renderer;
    /** Road: true (unnamed PVI points have no stable id). Trench: false (picks track by stable id). */
    _tracksUnnamedPviByRatio;
    _activity;
    _onChange;
    _state;
    _highlightedMarkerId;
    _disposed = false;
    constructor(args) {
        this._host = args.host;
        this._renderer = new SectionMarkerRenderer({
            viewer: args.viewer,
            site: args.site,
            owner: this._host,
        });
        this._tracksUnnamedPviByRatio = args.tracksUnnamedPviByRatio;
        this._activity = args.activity;
        this._onChange = args.onChange;
        this._state = {
            anchor: { type: "projectedDistance", distance: 0 },
            distFromStartPoint: 0,
            label: "",
        };
        this.select(this._bpAnchor());
    }
    /** Current selection: the anchor plus its resolved distances and label. */
    get state() {
        return this._state;
    }
    /** Selects the section identified by `anchor`, which also decides how it is tracked across edits. */
    select(anchor) {
        this._applySectionSelection(anchor);
    }
    /** Selects the section for a longitudinal-view click, choosing how the clicked item is tracked. */
    selectHit(hit) {
        switch (hit.type) {
            case "sectionMarker":
                // Station: raw distance already stays valid across edits.
                this.select(hit.markerKind === "station"
                    ? { type: "projectedDistance", distance: hit.projectedDistance }
                    : { type: "point", pointId: hit.markerId });
                return;
            case "controlPoint":
                this.select({ type: "point", pointId: hit.controlPointId });
                return;
            case "slopeBreak":
                // Only the Road longitudinal view produces slope-break hits.
                if (!this._tracksUnnamedPviByRatio) {
                    throw new Error("slopeBreak hit requires a host that tracks unnamed PVI points by ratio");
                }
                this.select({ type: "relativeDistance", ratio: hit.ratio });
                return;
            default:
                throw new Error(hit);
        }
    }
    /** Selects the section at a picked 3D centerline point, choosing how that point is tracked. */
    selectPickedPoint(pointId) {
        const sectionMarkers = toSectionMarkers(this._host.alignment.centerLinePoints);
        const marker = sectionMarkers.find((m) => m.id === pointId);
        if (marker) {
            // Station: raw distance already stays valid across edits.
            this.select(marker.kind === "station"
                ? {
                    type: "projectedDistance",
                    distance: marker.distanceFromStart,
                }
                : { type: "point", pointId: marker.id });
        }
        else {
            // IP projection or another centerline point outside sectionMarkers (e.g. BP/EP).
            const distance = getMarkerDistanceById(this._host.alignment, pointId);
            if (distance !== undefined) {
                if (this._tracksUnnamedPviByRatio &&
                    !isIpAssociatedMarker(this._host.alignment, pointId)) {
                    // Unnamed PVI point (ratio tracking): no stable id, track it by ratio.
                    this.select({
                        type: "relativeDistance",
                        ratio: distance / this._host.alignment.lengthOnDatum,
                    });
                }
                else {
                    // Track by the underlying IP's stable id, since the picked point's own id can be regenerated.
                    const stableId = this._host.alignment.getStableMarkerIdForPoint(pointId);
                    this.select({ type: "point", pointId: stableId });
                }
            }
        }
    }
    /** Re-resolves the current anchor after an alignment edit. */
    refresh() {
        this._applySectionSelection();
    }
    /** Restores the sphere color and removes the 3D panel. Terminal: pending updates are ignored. */
    dispose() {
        this._disposed = true;
        this._clearSelectedMarkerHighlight();
        this._renderer.dispose();
    }
    /** Anchor for BP, the default and fallback selection. */
    _bpAnchor() {
        const bpId = this._host.alignment.ipLinePoints[0]?.id;
        return bpId
            ? { type: "point", pointId: bpId }
            : { type: "projectedDistance", distance: 0 };
    }
    /** Resolves the anchor to its current 2D projected distance; falls back to BP when it no longer resolves. */
    _resolveSectionAnchor(anchor) {
        const lengthOnDatum = this._host.alignment.lengthOnDatum;
        switch (anchor.type) {
            case "point": {
                const distance = getMarkerDistanceById(this._host.alignment, anchor.pointId);
                if (distance !== undefined) {
                    return { anchor, projectedDistance: distance };
                }
                break;
            }
            case "projectedDistance":
                // Falls back once the station is gone (path shortened, station interval changed).
                if (hasCenterLinePointAt(this._host.alignment, anchor.distance, AlignmentPointType.STATION_POINT)) {
                    return { anchor, projectedDistance: anchor.distance };
                }
                break;
            case "relativeDistance": {
                const distance = anchor.ratio * lengthOnDatum;
                if (hasCenterLinePointAt(this._host.alignment, distance, AlignmentPointType.POINT_OF_VERTICAL_INTERSECTION)) {
                    return { anchor, projectedDistance: distance };
                }
                break;
            }
            default:
                throw new Error(anchor);
        }
        return { anchor: this._bpAnchor(), projectedDistance: 0 };
    }
    /** Applies the given anchor, or re-resolves the current one (after an edit) when omitted. */
    _applySectionSelection(requestedAnchor = this._state.anchor) {
        const { anchor, projectedDistance } = this._resolveSectionAnchor(requestedAnchor);
        const distanceFromStart3D = this._host.getCorrespondingDistanceFromStart(projectedDistance);
        const pathLength = this._host.getLength();
        const clampedDistance = Math.min(Math.max(distanceFromStart3D, 0), pathLength);
        const label = getMarkerLabel(this._host.alignment, projectedDistance, anchor.type === "point" ? anchor.pointId : undefined) ?? this._state.label;
        this._state = {
            anchor,
            distFromStartPoint: clampedDistance,
            label,
        };
        this._updateSelectedMarkerColor(projectedDistance);
        this._renderer.setSelectedDistance(clampedDistance).then(() => {
            if (this._disposed)
                return;
            this._onChange(this._state.distFromStartPoint, this._state.label);
        });
        ActivityLogger.log(this._activity);
    }
    /** Restores the previously highlighted sphere to its default color. */
    _clearSelectedMarkerHighlight() {
        if (this._highlightedMarkerId) {
            const renderableAlignment = this._host.cesiumRenderableAlignment;
            const entity = renderableAlignment.getCesiumCenterLineSphereEntity(this._highlightedMarkerId) ??
                renderableAlignment.getCesiumSphereEntity(this._highlightedMarkerId);
            if (entity?.point) {
                entity.point.color = new ConstantProperty(this._defaultSphereColor(entity));
            }
            this._highlightedMarkerId = undefined;
        }
    }
    /** Colors the selected sphere blue, reverting the previous one to its default color. */
    _updateSelectedMarkerColor(projectedDistance) {
        const renderableAlignment = this._host.cesiumRenderableAlignment;
        this._clearSelectedMarkerHighlight();
        const { anchor } = this._state;
        const currentPointId = anchor.type === "point"
            ? getMarkerCurrentId(this._host.alignment, anchor.pointId)
            : undefined;
        // BP/EP have no separate center-line sphere (skipped in favor of the control point sphere
        // sharing the same id), so fall back to the control point sphere for those.
        let entity = currentPointId
            ? (renderableAlignment.getCesiumCenterLineSphereEntity(currentPointId) ??
                renderableAlignment.getCesiumSphereEntity(currentPointId))
            : undefined;
        let resolvedId = currentPointId;
        if (!entity) {
            const sectionMarkers = toSectionMarkers(this._host.alignment.centerLinePoints);
            const marker = sectionMarkers.find((m) => Math.abs(m.distanceFromStart - projectedDistance) < 1e-3);
            if (marker) {
                entity = renderableAlignment.getCesiumSphereEntity(marker.id);
                resolvedId = marker.id;
            }
            else if (anchor.type === "relativeDistance") {
                // Unnamed PVI point: no stable id, resolve the sphere by distance.
                const pointId = getMarkerIdAt(this._host.alignment, projectedDistance);
                if (pointId) {
                    entity = renderableAlignment.getCesiumCenterLineSphereEntity(pointId);
                    resolvedId = pointId;
                }
            }
        }
        if (entity?.point) {
            entity.point.color = new ConstantProperty(getDefaultSectionMarkerColorSettings().selectedColor);
            this._highlightedMarkerId = resolvedId;
        }
    }
    /** Default color depends on whether the sphere is an IP control point or a center-line point. */
    _defaultSphereColor(entity) {
        const pickInfo = getEntityPickInfo(entity);
        const colors = getDefaultSectionMarkerColorSettings();
        return pickInfo?.pickableType === CESIUM_PICKABLE_ENTITY.ALIGNMENT_IP_POINT
            ? colors.ipControlPointColor
            : colors.markerColor;
    }
}
//# sourceMappingURL=section-selection-controller.js.map